import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, X } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

const empty = { title: "", description: "", price: "", image_url: "/images/g3.jpg", status: "draft" };

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/offers/${editing}`, form);
        toast.success("Offer updated");
      } else {
        await api.post("/offers", form);
        toast.success("Offer created");
      }
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch { toast.error("Failed to save offer"); }
  };

  const startEdit = (o) => { setEditing(o.id); setForm({ title: o.title, description: o.description, price: o.price, image_url: o.image_url, status: o.status }); setShowForm(true); };
  const remove = async (id) => { if (window.confirm("Delete this offer?")) { await api.delete(`/offers/${id}`); toast.success("Deleted"); load(); } };
  const changeStatus = async (id, status) => { await api.patch(`/offers/${id}`, { status }); load(); };

  return (
    <div data-testid="admin-offers-page">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="font-serif-display text-4xl text-[#5979A9]">Weekly Offers</h1>
          <p className="text-[#4A5568] text-sm mt-1">Only one offer can be published at a time.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }} data-testid="new-offer-button" className="btn-primary !py-2.5 !px-5 !text-sm">+ New Offer</button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {offers.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl border border-[#F0EAE6] overflow-hidden">
            <img src={o.image_url} alt={o.title} className="w-full h-44 object-cover" />
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-serif-display text-xl text-[#5979A9]">{o.title}</h3>
                <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full ${
                  o.status === "published" ? "bg-green-100 text-green-700" :
                  o.status === "archived" ? "bg-gray-100 text-gray-600" : "bg-yellow-100 text-yellow-700"
                }`}>{o.status}</span>
              </div>
              <p className="text-sm text-[#4A5568] line-clamp-2 mb-3">{o.description}</p>
              <p className="font-semibold text-[#E05947] mb-4">{o.price}</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => startEdit(o)} className="text-xs px-3 py-1.5 rounded-full bg-[#FAF4F1] hover:bg-[#F0EAE6] flex items-center gap-1"><Edit3 size={12}/>Edit</button>
                {o.status !== "published" && <button onClick={() => changeStatus(o.id, "published")} className="text-xs px-3 py-1.5 rounded-full bg-[#E05947] text-white">Publish</button>}
                {o.status === "published" && <button onClick={() => changeStatus(o.id, "archived")} className="text-xs px-3 py-1.5 rounded-full bg-gray-200">Archive</button>}
                <button onClick={() => remove(o.id)} className="text-xs px-3 py-1.5 rounded-full text-[#E05947] flex items-center gap-1"><Trash2 size={12}/>Delete</button>
              </div>
            </div>
          </div>
        ))}
        {offers.length === 0 && <p className="text-[#4A5568] col-span-full">No offers yet — click "New Offer".</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-3xl max-w-lg w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="offer-form-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif-display text-2xl text-[#5979A9]">{editing ? "Edit Offer" : "New Offer"}</h2>
              <button onClick={() => setShowForm(false)}><X size={20}/></button>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
              <Field label="Description" textarea value={form.description} onChange={(v) => setForm({ ...form, description: v })} required />
              <Field label="Price" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required placeholder="€18.50" />
              <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="offer-image"/>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <button type="submit" data-testid="offer-save-button" className="btn-primary w-full">Save Offer</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, textarea, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
                  className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2 resize-none" {...rest} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
               className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2" {...rest} />
      )}
    </div>
  );
}
